// Per-project scan rules — pick the project, edit the policy that
// drives which scanners run + when findings are treated as failures.
//
// v169 Tier 2: extended with scanner_config editors. Each Tier 2
// scanner has a small inline editor that reads/writes its sub-
// document inside scanner_config[scanner.id]:
//   * License: SPDX allow-list (chip input)
//   * Banned-API: pattern table (rule_id / regex / severity / message)
//   * Path excludes: glob list affecting all archive-walking scanners

import { useEffect, useState } from 'react';
import { Plus, Save, ScanLine, X } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useProjects } from '../../api/client';
import {
  useScanners,
  useScanRules,
  saveScanRules,
  type BannedApiPattern,
  type FindingSeverity,
  type ScanRules,
} from '../../api/detection';

const SEVERITIES: FindingSeverity[] = ['info', 'low', 'medium', 'high', 'critical'];

const selectClass =
  'h-10 px-3 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

const LICENSE_SUGGESTIONS = [
  'MIT', 'Apache-2.0', 'BSD-2-Clause', 'BSD-3-Clause', 'ISC',
  'MPL-2.0', 'LGPL-2.1', 'LGPL-3.0', 'GPL-2.0', 'GPL-3.0', 'AGPL-3.0',
  'Unlicense', 'EPL-2.0',
];

function costClassTone(costClass: string): 'success' | 'accent' | 'warning' {
  if (costClass === 'cheap') return 'success';
  if (costClass === 'medium') return 'accent';
  return 'warning';
}

function patternSeverityTone(severity: string): 'danger' | 'warning' | 'accent' {
  if (severity === 'critical' || severity === 'high') return 'danger';
  if (severity === 'medium') return 'warning';
  return 'accent';
}

export function DetectionRules() {
  const { data: projects } = useProjects();
  const [projectID, setProjectID] = useState<string>('');
  const { data: rules, mutate } = useScanRules(projectID || undefined);
  const { data: scanners } = useScanners();

  const [draft, setDraft] = useState<ScanRules | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (rules) setDraft({ ...rules });
  }, [rules]);

  useEffect(() => {
    if (!projectID && projects && projects.length > 0) {
      setProjectID(projects[0].id);
    }
  }, [projects, projectID]);

  const onSave = async () => {
    if (!draft || !projectID) return;
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      await saveScanRules(projectID, {
        enabled: draft.enabled,
        fail_on: draft.fail_on,
        scanner_set: draft.scanner_set,
        path_excludes: draft.path_excludes,
        suppressions: draft.suppressions,
        scanner_config: draft.scanner_config,
        sla_targets: draft.sla_targets,
      });
      await mutate();
      setSaved(true);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleScanner = (id: string, on: boolean) => {
    if (!draft) return;
    const set = new Set(draft.scanner_set);
    if (on) set.add(id); else set.delete(id);
    setDraft({ ...draft, scanner_set: Array.from(set) });
  };

  // --- scanner_config helpers ---

  const setScannerCfg = (scannerID: string, cfg: Record<string, unknown>) => {
    if (!draft) return;
    const next = { ...draft.scanner_config };
    if (Object.keys(cfg).length === 0) {
      delete next[scannerID];
    } else {
      next[scannerID] = cfg;
    }
    setDraft({ ...draft, scanner_config: next });
  };

  const licenseAllow: string[] = Array.isArray(draft?.scanner_config?.['orbital.license.v1']?.allow)
    ? (draft.scanner_config['orbital.license.v1'].allow as string[])
    : [];

  const bannedPatterns: BannedApiPattern[] = Array.isArray(draft?.scanner_config?.['orbital.banned-api.v1']?.patterns)
    ? (draft.scanner_config['orbital.banned-api.v1'].patterns as BannedApiPattern[])
    : [];

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <p className="orb-eyebrow text-accent/80 mb-2">Detection</p>
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-accent/10 border border-accent/30 flex items-center justify-center shrink-0">
              <ScanLine className="h-5 w-5 text-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-display font-semibold text-fg leading-tight">Scan rules</h1>
              <p className="text-fg-muted text-sm mt-2 max-w-2xl">
                Per-project policy. Empty defaults are safe: every enabled
                scanner runs and high-severity findings fail promotion.
              </p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader title="Project" />
          <CardBody>
            <select
              className={selectClass + ' w-full max-w-md'}
              value={projectID}
              onChange={(e) => setProjectID(e.target.value)}
            >
              <option value="">— select a project —</option>
              {projects?.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </CardBody>
        </Card>

        {draft && projectID && (
          <>
            <Card>
              <CardHeader title="Policy" />
              <CardBody className="space-y-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={draft.enabled}
                    onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                  />
                  <span className="text-sm text-fg">Detection enabled for this project</span>
                </label>
                <div>
                  <label htmlFor="rules-fail-on" className="block text-sm font-medium text-fg mb-1.5">Fail on severity</label>
                  <select
                    id="rules-fail-on"
                    className={selectClass + ' w-full max-w-xs'}
                    value={draft.fail_on}
                    onChange={(e) => setDraft({ ...draft, fail_on: e.target.value as FindingSeverity })}
                  >
                    {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <p className="text-xs text-fg-muted mt-1.5">
                    Findings ≥ this severity block promotions and surface as red badges.
                  </p>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="Scanners"
                description="Empty selection = run every cheap/medium scanner that's globally enabled. Heavy scanners (Trivy, Grype) only run when ticked here — they never run by default. Tick to opt in."
              />
              <CardBody>
                <ul className="space-y-2">
                  {(scanners ?? []).map((s) => {
                    const checked = draft.scanner_set.includes(s.id);
                    const isHeavy = s.cost_class === 'heavy';
                    return (
                      <li
                        key={s.id}
                        className={
                          'flex items-start gap-3 px-3 py-2 rounded border bg-bg-inset/40 ' +
                          (isHeavy && checked ? 'border-warning/40 bg-warning/5' : 'border-border')
                        }
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => toggleScanner(s.id, e.target.checked)}
                          className="mt-1"
                          disabled={!s.enabled}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs text-accent">{s.id}</span>
                            <Badge tone={costClassTone(s.cost_class)}>
                              {s.cost_class}
                            </Badge>
                            {!s.enabled && <Badge tone="neutral">disabled platform-wide</Badge>}
                            {isHeavy && <Badge tone="warning">opt-in only</Badge>}
                          </div>
                          <p className="text-xs text-fg-muted mt-1">{s.description}</p>
                          {isHeavy && checked && (
                            <p className="text-xs text-warning mt-1">
                              This is a heavy out-of-process scanner. Each scan
                              spools the artifact to disk and shells out — running
                              it on every artifact in the project may saturate the
                              dispatcher pool. Pair with a project-specific scanner_set
                              that excludes it from low-value repos.
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </CardBody>
            </Card>

            <SLATargetsEditor
              value={draft.sla_targets ?? {}}
              onChange={(sla_targets) => setDraft({ ...draft, sla_targets })}
            />

            <PathExcludesEditor
              value={draft.path_excludes}
              onChange={(path_excludes) => setDraft({ ...draft, path_excludes })}
            />

            <LicenseAllowEditor
              value={licenseAllow}
              onChange={(allow) => setScannerCfg('orbital.license.v1', allow.length === 0 ? {} : { allow })}
            />

            <BannedApiEditor
              value={bannedPatterns}
              onChange={(patterns) => setScannerCfg('orbital.banned-api.v1', patterns.length === 0 ? {} : { patterns })}
            />

            <div className="flex items-center justify-end gap-3">
              {saved && <span className="text-xs text-success">Saved.</span>}
              {err && <span className="text-xs text-danger font-mono">{err}</span>}
              <Button variant="primary" disabled={busy} onClick={onSave}>
                <Save className="h-4 w-4" />
                Save rules
              </Button>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}

function SLATargetsEditor({
  value,
  onChange,
}: {
  readonly value: Partial<Record<FindingSeverity, number>>;
  readonly onChange: (v: Partial<Record<FindingSeverity, number>>) => void;
}) {
  // The editor exposes a row per severity. Empty = no SLA for that
  // band (worker skips it). Saving an empty SLA-targets map means
  // "use platform defaults" — so to fully disable SLAs operators
  // set explicit zero on every row.
  const SEV_ORDER: FindingSeverity[] = ['critical', 'high', 'medium', 'low', 'info'];
  return (
    <Card>
      <CardHeader
        title="SLA targets (time to fix)"
        description="Per-severity day count. The hourly SLA worker stamps sla_breached_at when a finding's first_seen_at is older than its target and fires a finding.sla_breached webhook. Empty / 0 = no SLA for that band."
      />
      <CardBody>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {SEV_ORDER.map((s) => (
            <label key={s} className="flex flex-col gap-1">
              <span className="orb-section-title text-fg-muted">{s}</span>
              <Input
                type="number"
                min={0}
                placeholder="days"
                value={value[s] === undefined ? '' : String(value[s])}
                onChange={(e) => {
                  const next = { ...value };
                  if (e.target.value === '') {
                    delete next[s];
                  } else {
                    const n = Number.parseInt(e.target.value, 10);
                    if (!isNaN(n) && n >= 0) next[s] = n;
                  }
                  onChange(next);
                }}
              />
            </label>
          ))}
        </div>
        <p className="text-xs text-fg-muted mt-2">
          Defaults: critical 7 d, high 30 d, medium 90 d, low 365 d. Override per project if your team has different SLAs.
        </p>
      </CardBody>
    </Card>
  );
}

function PathExcludesEditor({ value, onChange }: { readonly value: string[]; readonly onChange: (v: string[]) => void }) {
  const [draft, setDraft] = useState('');
  return (
    <Card>
      <CardHeader
        title="Path excludes (archive walker)"
        description="Glob patterns matched against archive entry names. Skips entries before any Tier 2 scanner sees them. Examples: vendor/*, **/*.min.js, test/fixtures/*"
      />
      <CardBody>
        <div className="flex flex-wrap gap-2 mb-3">
          {value.length === 0 ? (
            <span className="text-xs text-fg-muted italic">No excludes — every entry walked.</span>
          ) : (
            value.map((p, i) => (
              <span key={p} className="inline-flex items-center gap-1 bg-bg-inset border border-border px-2 py-1 rounded text-xs font-mono">
                {p}
                <button
                  type="button"
                  className="text-fg-muted hover:text-danger"
                  onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="e.g. vendor/* or **/*.min.js"
            className="font-mono text-sm flex-1"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draft.trim()) {
                e.preventDefault();
                onChange([...value, draft.trim()]);
                setDraft('');
              }
            }}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={!draft.trim()}
            onClick={() => {
              onChange([...value, draft.trim()]);
              setDraft('');
            }}
          >
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function LicenseAllowEditor({ value, onChange }: { readonly value: string[]; readonly onChange: (v: string[]) => void }) {
  const [draft, setDraft] = useState('');
  return (
    <Card>
      <CardHeader
        title="License allow-list (orbital.license.v1)"
        description="SPDX identifiers permitted in this project. Empty = use built-in severity defaults (permissive=info, copyleft=high). With an allow-list, anything not on it becomes a high-severity finding."
      />
      <CardBody>
        <div className="flex flex-wrap gap-2 mb-3">
          {value.length === 0 ? (
            <span className="text-xs text-fg-muted italic">
              No allow-list configured — class-based severity defaults apply.
            </span>
          ) : (
            value.map((spdx, i) => (
              <span key={spdx} className="inline-flex items-center gap-1 bg-success/10 border border-success/30 text-success px-2 py-1 rounded text-xs font-mono">
                {spdx}
                <button
                  type="button"
                  className="text-success/60 hover:text-danger"
                  onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="SPDX ID, e.g. Apache-2.0"
            className="font-mono text-sm flex-1"
            list="license-suggestions"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draft.trim() && !value.includes(draft.trim())) {
                e.preventDefault();
                onChange([...value, draft.trim()]);
                setDraft('');
              }
            }}
          />
          <datalist id="license-suggestions">
            {LICENSE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
          </datalist>
          <Button
            type="button"
            variant="secondary"
            disabled={!draft.trim() || value.includes(draft.trim())}
            onClick={() => {
              onChange([...value, draft.trim()]);
              setDraft('');
            }}
          >
            <Plus className="h-4 w-4" />
            Allow
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function BannedApiEditor({ value, onChange }: { readonly value: BannedApiPattern[]; readonly onChange: (v: BannedApiPattern[]) => void }) {
  const [draft, setDraft] = useState<BannedApiPattern>({
    rule_id: '',
    regex: '',
    severity: 'medium',
    message: '',
  });

  const add = () => {
    if (!draft.rule_id.trim() || !draft.regex.trim()) return;
    onChange([...value, { ...draft, rule_id: draft.rule_id.trim(), regex: draft.regex.trim() }]);
    setDraft({ rule_id: '', regex: '', severity: 'medium', message: '' });
  };

  return (
    <Card>
      <CardHeader
        title="Banned-API patterns (orbital.banned-api.v1)"
        description="Project-defined regex patterns. The scanner runs each pattern against every text-like entry in archives uploaded to this project. Use this for house rules: no eval, no pickle.loads, no setRequestProperty('Authorization', ...) outside the auth package, etc."
      />
      <CardBody>
        {value.length === 0 ? (
          <p className="text-xs text-fg-muted italic mb-3">
            No patterns yet — the banned-api scanner is a no-op for this project until you add one.
          </p>
        ) : (
          <table className="w-full text-sm mb-4">
            <thead>
              <tr className="text-left border-b border-border">
                <th className="py-1.5 pr-3 font-medium text-fg-muted">Rule ID</th>
                <th className="py-1.5 pr-3 font-medium text-fg-muted">Regex</th>
                <th className="py-1.5 pr-3 font-medium text-fg-muted">Severity</th>
                <th className="py-1.5 pr-3 font-medium text-fg-muted">Message</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {value.map((p, i) => (
                <tr key={`${p.rule_id}|${p.regex}`} className="border-b border-border/40 last:border-0">
                  <td className="py-1.5 pr-3 font-mono text-xs text-accent">{p.rule_id}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs text-fg break-all max-w-xs">{p.regex}</td>
                  <td className="py-1.5 pr-3"><Badge tone={patternSeverityTone(p.severity)}>{p.severity}</Badge></td>
                  <td className="py-1.5 pr-3 text-fg-muted text-xs">{p.message ?? ''}</td>
                  <td className="py-1.5">
                    <button
                      type="button"
                      className="text-fg-muted hover:text-danger p-1"
                      onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                      aria-label="Remove pattern"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
          <Input
            placeholder="rule_id (e.g. no-eval)"
            value={draft.rule_id}
            onChange={(e) => setDraft({ ...draft, rule_id: e.target.value })}
          />
          <Input
            placeholder="regex (e.g. \\beval\\s*\\()"
            className="font-mono text-sm"
            value={draft.regex}
            onChange={(e) => setDraft({ ...draft, regex: e.target.value })}
          />
          <select
            className={selectClass}
            value={draft.severity}
            onChange={(e) => setDraft({ ...draft, severity: e.target.value as FindingSeverity })}
          >
            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <Input
            placeholder="message (optional)"
            value={draft.message ?? ''}
            onChange={(e) => setDraft({ ...draft, message: e.target.value })}
          />
        </div>
        <div className="mt-2 flex justify-end">
          <Button
            type="button"
            variant="secondary"
            disabled={!draft.rule_id.trim() || !draft.regex.trim()}
            onClick={add}
          >
            <Plus className="h-4 w-4" />
            Add pattern
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
